import React, { createContext, useState, useEffect, useContext, ReactNode, useCallback, useRef } from 'react';
import { User as FirebaseUser, onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase/firebase';
import { log } from '../services/production-logger';

// Import types directly
import { User, UserRole, UserStatus } from '../models/user';
import { UserProfile } from '../types/user';
import { saveUserProfile } from '../utils/expo-auth-persistence';
import { clearAuthData, isSessionValid, refreshAuthToken } from '../utils/auth-helpers';

// Lazy load services to prevent circular dependencies
let authService: any;
let userService: any;
let logEvent: any;
let featureFlagsService: any;
let pushNotificationService: any;

const getServices = () => {
  if (!authService) {
    authService = require('../services/auth-service').default;
    userService = require('../services/user-service').userService;
    logEvent = require('../services/analytics-service').logEvent;
    featureFlagsService = require('../services/feature-flags-service').default;
    pushNotificationService = require('../services/push-notification-service').default;
  }
  return { authService, userService, logEvent, featureFlagsService, pushNotificationService };
};
import { logAuthInfo, logAuthError, logAuthWarn, logAuthEvent } from '../utils/auth-logger';
import { initManager } from '../utils/initialization-manager';

interface AuthContextValue {
  currentUser: FirebaseUser | null;
  userProfile: UserProfile | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isAuthReady: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, displayName?: string) => Promise<void>;
  signInWithGoogle: () => Promise<FirebaseUser | undefined>;
  signInWithApple: () => Promise<FirebaseUser | undefined>;
  logout: () => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  updateProfile: (profile: Partial<UserProfile>) => Promise<void>;
  refreshUserProfile: () => Promise<void>;
}

// Create the auth context
export const AuthContext = createContext<AuthContextValue>({
  currentUser: null,
  userProfile: null,
  isLoading: true,
  isAuthenticated: false,
  isAuthReady: false,
  error: null,
  login: async () => {},
  register: async () => {},
  signInWithGoogle: async () => undefined,
  signInWithApple: async () => undefined,
  logout: async () => {},
  forgotPassword: async () => {},
  updateProfile: async () => {},
  refreshUserProfile: async () => {},
});

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<FirebaseUser | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isAuthReady, setIsAuthReady] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  
  // Track initialization state
  const isInitializing = useRef<boolean>(false);
  const hasInitialized = useRef<boolean>(false);

  // Helper to set error state
  const setAuthError = useCallback((error: Error | string) => {
    const errorMessage = typeof error === 'string' ? error : error.message;
    logAuthError('Auth context error', error);
    setError(errorMessage);
    setIsLoading(false);
  }, []);

  // Clear error state
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Login function
  const login = useCallback(async (email: string, password: string): Promise<void> => {
    try {
      setIsLoading(true);
      clearError();
      logAuthEvent('Login attempt started', { email });
      
      const { authService } = getServices();
      const result = await authService.login(email, password);
      
      if (result.success && result.user) {
        logAuthEvent('Login successful', { userId: result.user.uid });
        // Don't return the user, just return void as declared
        return;
      }
      throw new Error(result.message || 'Login failed');
    } catch (error) {
      logAuthError('Login failed', error);
      setAuthError(error as Error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [clearError, setAuthError]);

  // Register function
  const register = useCallback(async (email: string, password: string, displayName?: string): Promise<void> => {
    try {
      setIsLoading(true);
      clearError();
      logAuthEvent('Registration attempt started', { email });
      
      const { authService } = getServices();
      const user = await authService.createUserWithEmailAndPassword(email, password, displayName);
      
      logAuthEvent('Registration successful', { userId: user.uid });
    } catch (error) {
      logAuthError('Registration failed', error);
      setAuthError(error as Error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [clearError, setAuthError]);

  // Google Sign-In function
  const signInWithGoogle = useCallback(async (): Promise<FirebaseUser | undefined> => {
    try {
      setIsLoading(true);
      clearError();
      logAuthEvent('Google sign-in attempt started');
      
      const { authService } = getServices();
      const result = await authService.signInWithGoogle();
      
      if (result.success && result.user) {
        logAuthEvent('Google sign-in successful', { userId: result.user.uid });
        return result.user;
      }
      throw new Error(result.message || 'Google sign-in failed');
    } catch (error) {
      logAuthError('Google sign-in failed', error);
      setAuthError(error as Error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [clearError, setAuthError]);

  // Apple Sign-In function
  const signInWithApple = useCallback(async (): Promise<FirebaseUser | undefined> => {
    try {
      setIsLoading(true);
      clearError();
      logAuthEvent('Apple sign-in attempt started');
      
      const { authService } = getServices();
      const result = await authService.signInWithApple();
      
      if (result.success && result.user) {
        logAuthEvent('Apple sign-in successful', { userId: result.user.uid });
        return result.user;
      }
      throw new Error(result.message || 'Apple sign-in failed');
    } catch (error) {
      logAuthError('Apple sign-in failed', error);
      setAuthError(error as Error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [clearError, setAuthError]);

  // Logout function
  const logout = useCallback(async (): Promise<void> => {
    try {
      setIsLoading(true);
      logAuthEvent('Logout attempt started');
      
      const { authService } = getServices();
      await authService.signOut();
      
      // Clear all auth state
      setCurrentUser(null);
      setUserProfile(null);
      setIsAuthenticated(false);
      clearError();
      
      // Clear persisted auth data
      await clearAuthData();
      
      logAuthEvent('Logout successful');
    } catch (error) {
      logAuthError('Logout failed', error);
      setAuthError(error as Error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [clearError, setAuthError]);

  // Forgot password function
  const forgotPassword = useCallback(async (email: string): Promise<void> => {
    try {
      setIsLoading(true);
      clearError();
      logAuthEvent('Password reset attempt started', { email });
      
      const { authService } = getServices();
      await authService.sendPasswordResetEmail(email);
      
      logAuthEvent('Password reset email sent', { email });
    } catch (error) {
      logAuthError('Password reset failed', error);
      setAuthError(error as Error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [clearError, setAuthError]);

  // Update profile function
  const updateProfile = useCallback(async (profile: Partial<UserProfile>): Promise<void> => {
    try {
      setIsLoading(true);
      clearError();
      
      if (!currentUser) {
        throw new Error('No authenticated user');
      }
      
      logAuthEvent('Profile update attempt started', { userId: currentUser.uid });
      
      const { userService } = getServices();
      
      // Convert UserProfile partial to UserProfileFormData for the update
      const formData = {
        displayName: profile.displayName || undefined,
        photoURL: profile.photoURL || undefined,
        preferences: profile.preferences || undefined,
      };
      
      await userService.updateUserProfile(currentUser.uid, formData);
      
      // Get the updated profile in UserProfile format
      const updatedProfile = await userService.getUserProfile(currentUser.uid);
      
      setUserProfile(updatedProfile);
      if (updatedProfile) {
        await saveUserProfile(updatedProfile);
      }
      
      logAuthEvent('Profile update successful', { userId: currentUser.uid });
    } catch (error) {
      logAuthError('Profile update failed', error);
      setAuthError(error as Error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [currentUser, clearError, setAuthError]);

  // Refresh user profile function
  const refreshUserProfile = useCallback(async (): Promise<void> => {
    try {
      if (!currentUser) {
        return;
      }
      
      logAuthEvent('Profile refresh attempt started', { userId: currentUser.uid });
      
      const { userService } = getServices();
      const profile = await userService.getUserProfile(currentUser.uid);
      
      setUserProfile(profile);
      if (profile) {
        await saveUserProfile(profile);
      }
      
      logAuthEvent('Profile refresh successful', { userId: currentUser.uid });
    } catch (error) {
      logAuthError('Profile refresh failed', error);
      // Don't throw here as this might be called during initialization
    }
  }, [currentUser]);

  // Initialize authentication state
  const initializeAuth = useCallback(async () => {
    if (isInitializing.current || hasInitialized.current) {
      return;
    }
    
    isInitializing.current = true;
    
    try {
      logAuthInfo('Initializing auth state');
      
      // Initialize the initialization manager
      await initManager.initialize();
      
      // Set up Firebase auth state listener
      const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
        try {
          logAuthInfo('Firebase auth state changed', { 
            userId: firebaseUser?.uid, 
            email: firebaseUser?.email 
          });
          
          if (firebaseUser) {
            // User is signed in
            setCurrentUser(firebaseUser);
            
            // Check if session is valid
            if (!isSessionValid()) {
              logAuthWarn('Session expired, refreshing token');
              await refreshAuthToken();
            }
            
            // Load user profile
            const { userService } = getServices();
            try {
              logAuthInfo('Loading user profile...', { 
                userId: firebaseUser.uid,
                serviceAvailable: !!userService,
                hasGetUserProfile: typeof userService?.getUserProfile === 'function'
              });
              
              const profile = await userService.getUserProfile(firebaseUser.uid);
              
              logAuthInfo('Profile fetch result:', { 
                userId: firebaseUser.uid,
                profileExists: !!profile,
                profileType: typeof profile
              });
              
              setUserProfile(profile);
              setIsAuthenticated(true);
              
              if (profile) {
                await saveUserProfile(profile);
                logAuthInfo('User profile saved to local storage', { userId: firebaseUser.uid });
              } else {
                logAuthWarn('User profile is null - user may need to complete registration', { userId: firebaseUser.uid });
              }
              
              logAuthInfo('User profile loaded successfully', { userId: firebaseUser.uid });
            } catch (profileError) {
              logAuthError('Failed to load user profile', {
                error: profileError,
                errorMessage: profileError?.message,
                errorName: profileError?.name,
                userId: firebaseUser.uid,
                serviceType: typeof userService,
                methodExists: typeof userService?.getUserProfile
              });
              
              // Still set as authenticated even if profile fails
              setIsAuthenticated(true);
            }
            
            // Mark USER_PROFILE phase as complete (success or failure)
            initManager.markComplete('USER_PROFILE');
          } else {
            // User is signed out
            setCurrentUser(null);
            setUserProfile(null);
            setIsAuthenticated(false);
            await clearAuthData();
            logAuthInfo('User signed out, cleared auth state');
          }
          
          setIsAuthReady(true);
          setIsLoading(false);
          clearError();
          
          // Mark AUTH phase as complete
          initManager.markComplete('AUTH');
        } catch (error) {
          logAuthError('Auth state change handler error', error);
          setAuthError(error as Error);
          setIsAuthReady(true);
          setIsLoading(false);
          
          // Mark AUTH phase as complete even on error
          initManager.markComplete('AUTH');
        }
      });
      
      hasInitialized.current = true;
      logAuthInfo('Auth initialization completed');
      
      // Return cleanup function
      return unsubscribe;
    } catch (error) {
      logAuthError('Auth initialization failed', error);
      setAuthError(error as Error);
      setIsAuthReady(true);
      setIsLoading(false);
    } finally {
      isInitializing.current = false;
    }
  }, [clearError, setAuthError]);

  // Initialize auth on mount
  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    
    const init = async () => {
      const cleanup = await initializeAuth();
      if (cleanup) {
        unsubscribe = cleanup;
      }
    };
    
    init();
    
    // Cleanup on unmount
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [initializeAuth]);

  // Context value
  const value: AuthContextValue = {
    currentUser,
    userProfile,
    isLoading,
    isAuthenticated,
    isAuthReady,
    error,
    login,
    register,
    signInWithGoogle,
    signInWithApple,
    logout,
    forgotPassword,
    updateProfile,
    refreshUserProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthProvider;
